import { useState, useMemo, useCallback } from 'react'

import { useApolloClient } from '@apollo/client'
import { F0Box, F0Dialog, F0Text, F0Card } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import { FinanceBudgetsDetailBudgetFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { useCreateBudgetAllocation } from 'modules/finance/hooks/useBudgetAllocations'
import { handleResult } from 'modules/core/lib/handleMutation'
import { track } from 'modules/core/lib/Tracking'
import { events } from 'modules/finance/lib/events'
import { allocationsOverBudget } from 'modules/finance/lib/budget_allocations'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

type BudgetExceededOption = 'increaseLimit' | 'tryAnotherAllocation' | null

interface BudgetExceededResult {
  suggestedBudgetLimit: number
  suggestedAllocationCents: number
  originalAllocationCents: number
}

interface AddEmployeesModalProps {
  budget: FinanceBudgetsDetailBudgetFragment
  onClose: () => void
  employeeIdsWithBudget: string[]
}

export const AddEmployeesModal = ({
  budget,
  onClose,
  employeeIdsWithBudget,
}: AddEmployeesModalProps) => {
  const apolloClient = useApolloClient()
  const {
    access: { locale },
  } = useCurrentSession()

  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })
  const [limitPerEmployee, setLimitPerEmployee] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showBudgetExceededOptions, setShowBudgetExceededOptions] = useState(false)
  const [selectedOption, setSelectedOption] = useState<BudgetExceededOption>(null)
  const [budgetExceededResult, setBudgetExceededResult] = useState<BudgetExceededResult | null>(
    null
  )

  const { createBudgetAllocation } = useCreateBudgetAllocation({
    budgetId: budget.id,
  })

  const selectedCount = employeeSelection.type === 'manual' ? employeeSelection.ids.length : 0

  // Memoized budget values
  const budgetValues = useMemo(() => {
    const limitCents = Math.round((limitPerEmployee ?? 0) * 100)
    const budgetLimitCents = Number(budget.amountCents)
    const totalAllocatedCents = Number(budget.totalAllocatedAmountCents ?? 0)

    return {
      limitCents,
      budgetLimitCents,
      totalAllocatedCents,
      suggestedBudgetLimit: Math.round(limitCents * selectedCount + totalAllocatedCents),
      suggestedAllocationCents: Math.floor(
        (budgetLimitCents - totalAllocatedCents) / Math.max(selectedCount, 1)
      ),
    }
  }, [limitPerEmployee, budget.amountCents, budget.totalAllocatedAmountCents, selectedCount])

  const resetBudgetExceededState = useCallback(() => {
    setShowBudgetExceededOptions(false)
    setSelectedOption(null)
    setBudgetExceededResult(null)
  }, [])

  const handleSelectionChange = useCallback(
    (selection: EmployeeSelectorV2MultiSelectState) => {
      setEmployeeSelection(selection)
      setError(null)
      resetBudgetExceededState()
    },
    [resetBudgetExceededState]
  )

  const handleLimitChange = useCallback(
    (value: number | null) => {
      setLimitPerEmployee(value)
      setError(null)
      resetBudgetExceededState()
    },
    [resetBudgetExceededState]
  )

  const handleOptionSelect = (option: BudgetExceededOption) => {
    setSelectedOption(option)
    setError(null)
    setShowBudgetExceededOptions(false)
  }

  const handleSubmit = async () => {
    // Validation
    if (selectedCount === 0) {
      setError(i18n.t('budgets.detail.add_employee_modal.validate.no_employees'))
      return
    }

    if (!limitPerEmployee || limitPerEmployee <= 0) {
      setError(i18n.t('budgets.detail.allocation_per_employee_modal.validate.limit_greater'))
      return
    }

    // Check if allocation would exceed budget limit
    const wouldExceedBudget = allocationsOverBudget(
      budget.amountCents,
      budgetValues.limitCents,
      selectedCount,
      budgetValues.totalAllocatedCents
    )

    if (wouldExceedBudget && !selectedOption) {
      setError(
        i18n.t('budgets.detail.allocation_per_employee_modal.validate.budget_limit_exceeded')
      )
      setBudgetExceededResult({
        suggestedBudgetLimit: budgetValues.suggestedBudgetLimit,
        suggestedAllocationCents: budgetValues.suggestedAllocationCents,
        originalAllocationCents: budgetValues.limitCents,
      })
      setShowBudgetExceededOptions(true)
      return
    }

    setSubmitting(true)
    try {
      let amountCents = budgetValues.limitCents
      let newBudgetLimit: number | undefined

      if (selectedOption === 'increaseLimit' && budgetExceededResult) {
        amountCents = budgetExceededResult.originalAllocationCents
        newBudgetLimit = budgetExceededResult.suggestedBudgetLimit
      } else if (selectedOption === 'tryAnotherAllocation' && budgetExceededResult) {
        amountCents = budgetExceededResult.suggestedAllocationCents
      }

      const response = await createBudgetAllocation({
        budgetId: budget.id,
        amountCents,
        budgetLimitAmountCents: newBudgetLimit,
        employeeSelection,
      })

      const result = handleResult(response, () => {})

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('budgets.detail.add_employee_modal.submit_success'),
        })
        track(events.addEmployeesToBudget, { budgetId: budget.id })

        await apolloClient.refetchQueries({
          include: [
            'FinanceBudgetDetailEmployeesDataCollection',
            'FinanceBudgetDetailRevampPage',
            'FinanceBudgetDetailAmountWidgets',
          ],
        })

        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('budgets.detail.add_employee_modal.submit_error'),
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const modalTitle = showBudgetExceededOptions
    ? i18n.t('budgets.detail.add_employee_modal.budget_limit_exceeded.title')
    : i18n.t('budgets.detail.add_employee_modal.title')

  const modalDescription = showBudgetExceededOptions
    ? i18n.t('budgets.detail.add_employee_modal.budget_limit_exceeded.description')
    : i18n.t('budgets.detail.add_employee_modal.description')

  const displaySuggestedBudgetLimit = budgetExceededResult?.suggestedBudgetLimit ?? 0
  const displaySuggestedAllocationCents = budgetExceededResult?.suggestedAllocationCents ?? 0

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={modalTitle}
      width="xl"
      primaryAction={{
        label: i18n.t('common.save'),
        onClick: handleSubmit,
        disabled:
          submitting || selectedCount === 0 || (showBudgetExceededOptions && !selectedOption),
        loading: submitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        <F0Text variant="body" content={modalDescription} />

        {!showBudgetExceededOptions && (
          <>
            <NumberInput
              label={i18n.t('budgets.detail.allocation_per_employee_modal.limit_per_employee')}
              locale={i18n.locale}
              units={budget.currency}
              value={limitPerEmployee ?? undefined}
              onChange={handleLimitChange}
              maxDecimals={2}
              size="md"
              status={error ? { type: 'error', message: error } : undefined}
            />

            <EmployeeSelectorV2
              multiple
              value={employeeSelection.type === 'manual' ? employeeSelection.ids : undefined}
              onChange={handleSelectionChange}
              forcedFilters={{ excludeIds: employeeIdsWithBudget }}
            />

            {selectedCount > 0 && (
              <F0Text
                variant="small"
                color="secondary"
                content={i18n.t('budgets.detail.add_employee_modal.employees_selected', {
                  count: selectedCount,
                })}
              />
            )}
          </>
        )}

        {showBudgetExceededOptions && (
          <F0Box display="flex" flexDirection="column" gap="lg">
            <F0Text
              variant="label"
              content={i18n.t('budgets.budget_exceeded_option_selector.title')}
            />
            <F0Text
              variant="body"
              color="secondary"
              content={i18n.t('budgets.budget_exceeded_option_selector.description')}
            />

            <F0Card
              selectable
              selected={selectedOption === 'increaseLimit'}
              onSelect={() => handleOptionSelect('increaseLimit')}
              title={i18n.t('budgets.budget_exceeded_option_selector.card_1.title')}
              description={i18n.t('budgets.budget_exceeded_option_selector.card_1.description', {
                previousLimit: amountWithCurrency(
                  Math.round(budgetValues.budgetLimitCents),
                  budget.currency,
                  locale
                ),
                newLimit: amountWithCurrency(
                  Math.round(displaySuggestedBudgetLimit),
                  budget.currency,
                  locale
                ),
              })}
            />

            {!isNaN(displaySuggestedAllocationCents) && displaySuggestedAllocationCents > 0 && (
              <F0Card
                selectable
                selected={selectedOption === 'tryAnotherAllocation'}
                onSelect={() => handleOptionSelect('tryAnotherAllocation')}
                title={i18n.t('budgets.budget_exceeded_option_selector.card_2.title')}
                description={i18n.t('budgets.budget_exceeded_option_selector.card_2.description', {
                  newAmount: amountWithCurrency(
                    Math.round(displaySuggestedAllocationCents),
                    budget.currency,
                    locale
                  ),
                })}
              />
            )}
          </F0Box>
        )}
      </F0Box>
    </F0Dialog>
  )
}
